import React, {useEffect, useMemo} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useReport } from '../../Data/Reports';
import { Sections } from '../../Components/Containers/Section';
import { PageLoading } from '../../Components/Loading';
import { useServices } from '../../Data/Services';
import MultiTimeSeriesChart from './Components/MultiTimeSeriesChart';
import { selectableFutureDateRanges, selectablePeriods } from '../../Data/DateRanges';
import { usePreference } from '../../Data/Preference';
import BarPieTableChart from './Components/BarPieTableChart';

const Forecast = () => {
    const [dateRange, setDateRange] = usePreference('reports:forecast:dateRange', 'nextYear');
    const [groupBy, setGroupBy] = usePreference('reports:forecast:groupBy', 'auto');
    const [show, setShow] = usePreference('reports:forecast:show', 'value');
    const {report, refresh, isLoading} = useReport('/jobs/report/forecast', {dateRange, groupBy});
    const {services} = useServices(true);

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, groupBy]);


    return <>
        <TopBar left="/reports"
            title="Forecast"
            dropdownLoading={isLoading}
            dropdownOptions={[
                {
                    section: 'Show'
                },
                {
                    title: 'Value',
                    value: 'value',
                    onClick: () => setShow('value'),
                    icon: 'value' === show ? 'CircleCheck' : 'Circle',
                },
                {
                    title: 'Count',
                    value: 'count',
                    onClick: () => setShow('count'),
                    icon: 'count' === show ? 'CircleCheck' : 'Circle',
                },
                {
                    section: 'Date Range'
                },
                ...selectableFutureDateRanges.map(r => ({
                    title: r.label,
                    value: r.value,
                    onClick: () => setDateRange(r.value),
                    icon: r.value === dateRange ? 'CircleCheck' : 'Circle',
                })),
                {
                    section: 'Group by'
                },
                {
                    title: 'Auto',
                    value: 'auto',
                    onClick: () => setGroupBy('auto'),
                    icon: 'auto' === groupBy ? 'CircleCheck' : 'Circle',
                },
                ...selectablePeriods.map(r => ({
                    title: r.label,
                    value: r.value,
                    onClick: () => setGroupBy(r.value),
                    icon: r.value === groupBy ? 'CircleCheck' : 'Circle',
                })),
            ]}
        />
        <Page>
            {(report && services) ? <Inner {...{report, show, services}} /> : <PageLoading />}
        </Page>
    </>
}

const Inner = ({report, show, services}) => {
    const getServiceName = (srv) => services ? (services.find(s => s.type === srv)?.title ?? srv) : srv;
    const title = show === 'count' ? 'Count' : 'Total value';
    const dataType = show === 'count' ? 'number' : 'currency';
    const field = show === 'count' ? 'count' : 'total';

    const data = useMemo(() => {
        return {
            serviceChart: Object.keys(report.byServiceAndDates).map(service => {
                return {
                    dataLabel: getServiceName(service),
                    data: report.byServiceAndDates[service].map(d => ({
                        label: d.date,
                        value: d[field]
                    }))
                }
            }),
            statusChart: Object.keys(report.byStatusAndDates).map(status => {
                return {
                    dataLabel: status,
                    data: report.byStatusAndDates[status].map(d => ({
                        label: d.date,
                        value: d[field]
                    }))
                }
            }),
            services: Object.keys(report.byServiceAndDates).map(service => ({
                label: getServiceName(service),
                value: report.byServiceAndDates[service].reduce(
                    (acc, r) => acc + r[field],
                    0
                )
            })),
            dates: Object.values(report.byServiceAndDates).reduce((acc, service) => {
                service.forEach(date => {
                    acc[date.date] = acc[date.date] || 0;
                    acc[date.date] += date[field];
                });
                return acc;
            }, {})
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report.byServiceAndDates, show]);

    return <Sections>
        <MultiTimeSeriesChart title="Jobs by service over time"
            subtitle={`${title} of jobs over time, group by service`}
            dataType={dataType}
            data={data.serviceChart}
            hideEmptyDatasets
        />
        <MultiTimeSeriesChart title="Jobs by status over time"
            subtitle={`${title} of jobs over time, grouped by status`}
            dataType={dataType}
            data={data.statusChart}
            hideEmptyDatasets
        />
        <BarPieTableChart title="Service"
            dataLabel="Jobs"
            groupingLabel="Service"
            dataType={dataType}
            subtitle={`${title} of forecast jobs by service`}
            labelValueData={data.services}
            hideEmptyDatasets
        />
        <BarPieTableChart title="Dates"
            dataLabel="Jobs"
            groupingLabel="Service"
            dataType={dataType}
            subtitle={`${title} of forecast jobs by date`}
            labelValueData={Object.keys(data.dates).map(s => ({label: s, value: data.dates[s]}))}
            sort={false}
            hideEmptyDatasets
            dateLabels
        />
    </Sections>
}

export default Forecast;
